import React, { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { CreditCard, Shield, Loader2 } from 'lucide-react';
import api from '../../services/api';
import './PaymentMethodModal.scss';

const PaymentMethodModal = ({ 
  isOpen, 
  onClose, 
  onSelectPaymentMethod, 
  planName, 
  amount,
  planPrice, // 兼容PricingPlans传递的参数
  currency = 'USD',
  planId,
  billingCycle,
  isSubscription = false
}) => {
  const { t } = useI18n();
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handlePaymentMethodSelect = async (method) => {
    // 防止重复点击
    if (isProcessing) {
      return;
    }

    try {
      setIsProcessing(true);
      
      if (method === 'paypal') {
        // PayPal需要显示loading，因为要等待API响应和跳转
        await handlePayPalRedirect();
      } else if (method === 'stripe') {
        // Stripe也需要显示loading，因为要等待API响应和跳转
        await handleStripeRedirect();
      } else {
        // 其他支付方式直接处理
        onSelectPaymentMethod(method);
        onClose();
      }
    } catch (error) {
      console.error('Payment method selection error:', error);
      // 重置状态以允许重试
      setIsProcessing(false);
    }
  };

  const handleStripeRedirect = async () => {
    try {
      console.log('Creating Stripe checkout session...');
      
      let response, data;
      
      // 区分一次性支付和订阅支付
      if (billingCycle === 'credits' || !isSubscription) {
        // 积分包购买或一次性支付 - 使用新的v2接口
        console.log('Creating Stripe one-time payment with new v2 API');
        
        const paymentData = {
          packageId: planId,
          gateway: 'STRIPE'
        };
        
        response = await api.post('/v2/payment/create-credit-order', paymentData);
        
      } else {
        // 订阅支付 - 使用新的v2接口
        console.log('Creating Stripe subscription payment with new v2 API');
        
        const subscriptionData = {
          planId: planId,
          gateway: 'STRIPE'
        };
        
        response = await api.post('/v2/payment/create-subscription-order', subscriptionData);
      }

      data = response.data;
      console.log('Stripe response:', data);

      if (data.success && data.checkoutUrl) {
        console.log('Redirecting to Stripe:', data.checkoutUrl);
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received from Stripe');
      }
    } catch (error) {
      console.error('Stripe redirect error:', error);
      setIsProcessing(false);
      throw error;
    }
  };

  const handlePayPalRedirect = async () => {
    try {
      console.log('Creating PayPal redirect payment...');
      
      let response, data;
      
      // 区分一次性支付和订阅支付
      if (billingCycle === 'credits' || !isSubscription) {
        // 积分包购买或一次性支付 - 使用新的v2接口
        console.log('Creating PayPal one-time payment with new v2 API');
        
        const paymentData = {
          packageId: planId,
          gateway: 'PAYPAL'
        };
        
        response = await api.post('/v2/payment/create-credit-order', paymentData);
        
      } else {
        // 订阅支付 - 使用新的v2接口
        console.log('Creating PayPal subscription payment with new v2 API');
        
        const subscriptionData = {
          planId: planId,
          gateway: 'PAYPAL'
        };
        
        response = await api.post('/v2/payment/create-subscription-order', subscriptionData);
      }

      data = response.data;
      console.log('PayPal response:', data);

      if (data.success && data.checkoutUrl) {
        console.log('Redirecting to PayPal:', data.checkoutUrl);
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received from PayPal');
      }
    } catch (error) {
      console.error('PayPal redirect error:', error);
      setIsProcessing(false);
      throw error;
    }
  };

  // 使用amount或planPrice，优先使用amount
  const displayAmount = amount || planPrice;

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal-content">
        {/* Loading遮罩层 */}
        {isProcessing && (
          <div className="payment-loading-overlay">
            <div className="payment-loading-content">
              <Loader2 className="loading-spinner" size={40} />
              <p className="loading-text">{t('payment.processing')}</p>
            </div>
          </div>
        )}
        
        <div className="payment-modal-header">
          <h2 className="payment-modal-title">{t('payment.selectMethod')}</h2>
          <button 
            className="payment-modal-close" 
            onClick={onClose}
            disabled={isProcessing}
          >
            ✕
          </button>
        </div>

        <div className="payment-modal-body">
          <div className="payment-summary">
            <h3 className="payment-summary-title">{t('payment.orderSummary')}</h3>
            <div className="payment-summary-item">
              <span className="payment-summary-label">{planName}</span>
              <span className="payment-summary-amount">
                ${displayAmount} {currency}
              </span>
            </div>
          </div>

          <h3 className="payment-methods-title">{t('payment.choosePaymentMethod')}</h3>
          
          <div className="payment-method-options">
            <button
              className="payment-method-option"
              onClick={() => handlePaymentMethodSelect('stripe')}
              disabled={isProcessing}
            >
              <div className="payment-method-icon">
                <CreditCard size={24} />
              </div>
              <div className="payment-method-info">
                <div className="payment-method-name">{t('payment.creditCard')}</div>
                <p className="payment-method-description">
                  {t('payment.creditCardDescription')}
                </p>
              </div>
              <div className="payment-method-logos">
                <span className="payment-logo">Visa</span>
                <span className="payment-logo">Mastercard</span>
              </div>
            </button>

            <button
              className="payment-method-option"
              onClick={() => handlePaymentMethodSelect('paypal')}
              disabled={isProcessing}
            >
              <div className="payment-method-icon">
                <div className="paypal-logo">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a.641.641 0 0 1-.633-.74L23.696.901C23.778.382 24.226 0 24.75 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                  </svg>
                </div>
              </div>
              <div className="payment-method-info">
                <div className="payment-method-name">PayPal</div>
                <p className="payment-method-description">
                  {t('payment.paypalDescription')}
                </p>
              </div>
              <div className="payment-method-logos">
                <span className="payment-logo paypal">PayPal</span>
              </div>
            </button>
          </div>
        </div>

        <div className="payment-modal-footer">
          <p className="payment-security-note">
            <Shield className="security-icon" size={16} />
            {t('payment.securityNote')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;
